import {
  Alert,
  Box,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  Pagination,
  Typography,
  styled,
} from '@mui/material';
import { AppliedFilters, SearchControls } from 'src/components/Search';
import {
  ChangeEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { SearchProvider, useSearch, useSiteContext } from 'src/store';

import AppCard from 'src/components/AppCard';
import Breadcrumbs from 'src/components/Breadcrumbs';
import { customColorPalette, CardList } from '@orcha/micro-library-core-ui';
import { SortBy } from '@orcha/micro-library-search-ui';
import ProtectedPageRoute from 'src/utilities/auth/protectedPageRoute';
import RootWidgetLayout from 'src/components/layouts/RootWidgetLayout';
import { SearchSortByEnum } from '@orcha/types';
import { Translate } from 'next-translate';

import { enumFromStringValue } from '@orcha/utils';
import theme from 'src/themes/theme';
import { useRouter } from 'next/router';
import useSearchData from 'src/hooks/useSearchData';
import useSortByOptions from 'src/hooks/useSortByOptions';
import useTranslation from 'next-translate/useTranslation';
import { PageContentContainer } from '../components/common/styles/PageLayout';

export const SearchResultsHeader = styled(CardContent)(() => ({
  background: customColorPalette.blueGreyLight,
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  },
  [theme.breakpoints.up('lg')]: {
    padding: `${theme.spacing(3.75)} ${theme.spacing(4)}`,
  },
}));

export const SearchResultsContainer = styled(CardContent)(() => ({
  background: theme.palette.common.white,

  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(2)} ${theme.spacing(0.5)}`,
  },
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  },
  [theme.breakpoints.up('lg')]: {
    padding: `${theme.spacing(3.75)} ${theme.spacing(4)}`,
  },
}));

const SearchResultsPage = () => {
  const { t } = useTranslation('search');

  const {
    searchTerm,
    appliedFilters,
    sortBy,
    setSortBy,
    currentPage,
    setCurrentPage,
  } = useSearch();
  const sortByOptions = useSortByOptions();

  const router = useRouter();
  const { locale, asPath, pathname } = router;

  const { countryOfUse, searchConfig } = useSiteContext();
  const [localSearchTerm, setLocalSearchTerm] = useState<string | undefined>(
    searchTerm
  );
  const isSearchTermEmpty = !localSearchTerm || localSearchTerm === '';

  const PAGE_SIZE = 12;

  const filterQuery = appliedFilters
    ?.filter((f) => f.selectedOptions.length > 0)
    .map((filter) => {
      return filter.selectedOptions
        .map((s) => `&${filter.filterName}=${encodeURIComponent(s)}`)
        .join('');
    })
    .join('');
  const filterQueryString = filterQuery ? `${filterQuery}` : '';
  const localeQueryString = locale ? `&language=${locale}` : '';
  const countryQueryString = countryOfUse
    ? `&country=${countryOfUse.code}`
    : '';
  const sortByQueryString = sortBy ? `&sortBy=${sortBy}` : '';
  const searchTermQueryString = localSearchTerm
    ? `&searchTerm=${encodeURIComponent(localSearchTerm)}`
    : '';

  const { searchResults, isLoading, error } = useSearchData(
    currentPage,
    PAGE_SIZE,
    undefined,
    `${filterQueryString}${sortByQueryString}${searchTermQueryString}${localeQueryString}${countryQueryString}`
  );

  const totalItems = searchResults?.paging?.totalItems ?? 0;
  const totalPageCount = searchResults?.paging?.totalPages ?? 0;

  const updatePageURL = useCallback(() => {
    const sortByQuery = sortBy ? `&sortBy=${sortBy}` : '';
    const fullQuery = `${
      localSearchTerm ? encodeURIComponent(localSearchTerm) : localSearchTerm
    }${
      currentPage > 1 ? `&page=${currentPage}` : ''
    }${sortByQuery}${filterQueryString}`;

    const queryString = fullQuery ? `search=${fullQuery}` : '';

    router.replace({ pathname, query: queryString }, undefined, {
      shallow: true,
      locale,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearchTerm, currentPage, filterQueryString, sortBy, locale, asPath]);

  useEffect(() => {
    updatePageURL();
  }, [updatePageURL]);

  useEffect(() => {
    if (totalPageCount === 0) {
      return;
    }

    if (totalPageCount < currentPage && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [currentPage, setCurrentPage, totalPageCount]);

  const handlePaginationChange = (
    _event: ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    if (searchTerm === localSearchTerm) {
      return;
    }

    setLocalSearchTerm(searchTerm);
    setCurrentPage(1);

    const isNewSearchTermEmpty = !searchTerm || searchTerm === '';

    if (isNewSearchTermEmpty && sortBy === SearchSortByEnum.RELEVANCE) {
      const defaultSortOption = searchConfig?.orderByOptions?.find(
        (option) => option.isDefault
      );
      setSortBy(
        defaultSortOption
          ? enumFromStringValue<SearchSortByEnum>(
              SearchSortByEnum,
              defaultSortOption.optionName
            )
          : SearchSortByEnum.SCORE_DESCENDING
      );
    }

    if (!isNewSearchTermEmpty && sortBy !== SearchSortByEnum.RELEVANCE) {
      const defaultSortOption = searchConfig?.orderByOptions?.find(
        (option) => option.isDefault
      );
      setSortBy(
        defaultSortOption
          ? enumFromStringValue<SearchSortByEnum>(
              SearchSortByEnum,
              defaultSortOption.optionName
            )
          : SearchSortByEnum.RELEVANCE
      );
    }
  };

  const onSortByChange = (selectedValue: string | undefined) => {
    if (selectedValue !== sortBy) {
      setSortBy(selectedValue as SearchSortByEnum);
      setCurrentPage(1);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Breadcrumbs
          breadcrumbs={[
            {
              text: t('microsite:breadcrumbs.home'),
              href: '/',
            },
            {
              text: t('microsite:breadcrumbs.search'),
              disabled: true,
            },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <SearchResultsHeader>
          <PageContentContainer>
            <SearchControls handleSearch={handleSearch} size="small" />
          </PageContentContainer>
        </SearchResultsHeader>
        <SearchResultsContainer>
          {error && <Alert severity="error">{t('searchResultsError')}</Alert>}

          <PageContentContainer>
            <Grid container spacing={2}>
              {appliedFilters &&
                appliedFilters.filter((a) => a.selectedOptions.length > 0)
                  ?.length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        [theme.breakpoints.down('sm')]: {
                          padding: `0 ${theme.spacing(2)}`,
                        },
                      }}
                    >
                      <AppliedFilters />
                    </Box>
                  </Grid>
                )}
              <Grid container item justifyContent="space-between">
                <Grid item>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      [theme.breakpoints.down('sm')]: {
                        padding: `0 ${theme.spacing(2)}`,
                        marginBottom: theme.spacing(2),
                      },
                    }}
                  >
                    <Typography
                      color="primary"
                      fontWeight={700}
                      data-testid="search-results-total-items"
                    >
                      {totalItems}
                    </Typography>
                    <Typography fontWeight={500}>
                      {t('searchResultsCount', {
                        count: totalItems,
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      [theme.breakpoints.down('sm')]: {
                        padding: `0 ${theme.spacing(2)}`,
                      },
                    }}
                  >
                    <SortBy
                      handleSelectedChange={onSortByChange}
                      value={sortBy}
                      isRelevanceDisabled={isSearchTermEmpty}
                      label={t('search:sortBy.label')}
                      options={sortByOptions}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {isLoading ? (
                  <Box sx={{ display: 'flex' }}>
                    <CircularProgress
                      color="primary"
                      size="6rem"
                      sx={{ margin: '0 auto' }}
                      data-testid="circular-progress-loader"
                    />
                  </Box>
                ) : (
                  <CardList>
                    {searchResults?.apps?.map((appData) => (
                      <AppCard key={appData.appId} appData={appData} />
                    ))}
                  </CardList>
                )}
              </Grid>
            </Grid>
          </PageContentContainer>

          {totalPageCount > 1 && searchResults && (
            <CardActions
              sx={{
                padding: `${theme.spacing(6.25)} ${theme.spacing(
                  0
                )} ${theme.spacing(0)}`,
                display: 'flex',
              }}
            >
              <Pagination
                data-testid="search-pagination"
                sx={{ margin: 'auto' }}
                count={totalPageCount}
                page={currentPage}
                onChange={handlePaginationChange}
                disabled={totalPageCount < 2}
              />
            </CardActions>
          )}
        </SearchResultsContainer>
      </Grid>
    </Grid>
  );
};

export const SearchPageWithContext = () => {
  const router = useRouter();
  const { searchConfig } = useSiteContext();

  const {
    search,
    sortBy,
    page,
    healthCategories,
    costTypes,
    intendedAudiences,
    generalFeatures,
    clinicalFeatures,
    accreditationTypes,
    platforms,
    countryOfOrigins,
    commissionableStatuses,
    isCommissioned,
  } = router.query;

  const filtersQuery = {
    healthCategories,
    costTypes,
    intendedAudiences,
    generalFeatures,
    clinicalFeatures,
    accreditationTypes,
    platforms,
    countryOfOrigins,
    commissionableStatuses,
    isCommissioned,
  };

  const appliedFilters = Object.keys(filtersQuery)?.map((key: string) => {
    const { [key as keyof typeof filtersQuery]: matchingQuery } = filtersQuery;
    const queryValue = matchingQuery ?? [];
    const queryValueArray = Array.isArray(queryValue)
      ? queryValue
      : [queryValue];
    return { filterName: key, selectedOptions: queryValueArray };
  });
  const searchQuery = search ? search.toString() : '';
  let sortByQuery = sortBy
    ? enumFromStringValue<SearchSortByEnum>(SearchSortByEnum, sortBy.toString())
    : undefined;

  if (!sortByQuery) {
    const configuredDefaultSortBy = searchConfig?.orderByOptions?.find(
      (option) => option.isDefault
    );

    const defaultSortBy =
      searchQuery && searchQuery !== ''
        ? SearchSortByEnum.RELEVANCE
        : SearchSortByEnum.SCORE_DESCENDING;

    sortByQuery = configuredDefaultSortBy
      ? enumFromStringValue<SearchSortByEnum>(
          SearchSortByEnum,
          configuredDefaultSortBy.optionName
        )
      : defaultSortBy;
  }

  const pageQuery = page ? Number(page.toString()) : undefined;

  return (
    <SearchProvider
      initialSearchTerm={searchQuery}
      initialSortBy={sortByQuery}
      initialPage={pageQuery}
      initialAppliedFilters={appliedFilters}
    >
      <SearchResultsPage />
    </SearchProvider>
  );
};

export const getServerSideProps = async (context) =>
  ProtectedPageRoute(context, async () => {
    const props = {};

    return props;
  });

SearchPageWithContext.getLayout = (page: ReactElement, t: Translate) => {
  return (
    <RootWidgetLayout pageTitle={t('search:pageTitle')}>
      {page}
    </RootWidgetLayout>
  );
};

export default SearchPageWithContext;
